// Component: LivePriceBadge.jsx
// Purpose: Compact live-price pill rendered next to a deployed strategy.
//          Four visual variants driven by useDeploymentLtp state:
//            pending  — '—' placeholder
//            live     — animated digits + colored change row
//            stale    — 70% opacity, no flash
//            closed   — last price + CLOSED tag, 50% opacity

import React from 'react';
import TickerPrice from '../common/TickerPrice';
import { useDeploymentLtp } from '../../contexts/TradingDataContext';

const formatChange = (change, pct) => {
  if (change == null || pct == null) return null;
  const sign = change >= 0 ? '+' : '';
  return `${sign}${change.toFixed(2)} (${sign}${pct.toFixed(2)}%)`;
};

const ArrowIcon = ({ direction }) => {
  if (direction === 'up')   return <span className="text-[#00C292]">↑</span>;
  if (direction === 'down') return <span className="text-[#E46A76]">↓</span>;
  return null;
};

const LivePriceBadge = ({ deploymentId, compact = true, showSymbol = false }) => {
  const tick = useDeploymentLtp(deploymentId);
  if (!tick) return null;

  const { ltp, change, changePct, direction, symbol, state, resolvedInstrumentId } = tick;

  //--> Variant styling
  const isStaleOrClosed = state === 'stale' || state === 'closed';
  const opacityClass = state === 'closed' ? 'opacity-50' : state === 'stale' ? 'opacity-70' : '';
  const baseColor = direction === 'up' ? '#00C292' : direction === 'down' ? '#E46A76' : undefined;

  //--> Pending state
  //! Zero/absent LTP defense-in-depth — treat ltp <= 0 the same as null/undefined
  //! so a leaked zero tick renders the placeholder, never ₹0.00.
  if (state === 'pending' || ltp == null || ltp <= 0) {
    return (
      <div className={`inline-flex items-center gap-1.5 text-sm ${compact ? 'py-0.5 px-2' : 'py-1 px-3'}`}>
        {showSymbol && symbol && (
          <span className="text-xs font-medium text-fg-2">{symbol}</span>
        )}
        <span className="font-mono text-gray-400">—</span>
      </div>
    );
  }

  const changeText = formatChange(change, changePct);

  return (
    <div className={`inline-flex items-center gap-2 text-sm font-medium ${opacityClass} ${compact ? 'py-0.5 px-2' : 'py-1 px-3'}`}>
      {showSymbol && symbol && (
        <span className="text-xs font-medium text-fg-2">{symbol}</span>
      )}
      <TickerPrice
        //--> key forces remount on rollover so prevValueRef resets and the first
        //*   tick of the new contract doesn't produce a spurious direction flash.
        key={resolvedInstrumentId}
        value={ltp}
        baseColor={baseColor}
        //--> Direction-aware className so non-digit glyphs (₹, comma, decimal)
        //*   fade together with the digits — TickerDigit only animates color on
        //*   numeric chars; the rest inherit this class directly. Matches the
        //*   WatchList palette convention.
        className={
          direction === 'up'   ? 'text-[#00C292]' :
          direction === 'down' ? 'text-[#E46A76]' :
          'text-fg-1'
        }
      />
      {!isStaleOrClosed && <ArrowIcon direction={direction} />}
      {state === 'closed' && (
        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">CLOSED</span>
      )}
      {state !== 'closed' && changeText && (
        <span className={`text-xs ${direction === 'up' ? 'text-[#00C292]' : direction === 'down' ? 'text-[#E46A76]' : 'text-gray-500'}`}>
          {changeText}
        </span>
      )}
    </div>
  );
};

export default LivePriceBadge;
